import { inject, Injectable, LOCALE_ID, NgZone } from '@angular/core';
import { HttpTransportType, HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import {
  asyncScheduler,
  BehaviorSubject,
  catchError,
  combineLatest,
  concatMap,
  delay,
  distinctUntilChanged,
  EMPTY,
  filter,
  finalize,
  from,
  fromEvent,
  map,
  merge,
  NEVER,
  Observable,
  of,
  retry,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
  throttleTime,
  throwError,
  timeout,
  timer
} from 'rxjs';
import { I_RequestMessage, I_WebsocketParams } from '../../../models/ws-message.interface';
import { UtilsService } from '../../../utils/utils.service';
import { AuthService } from '../../auth.service';
import { AlphanumericCounter } from '../idGenerator.service';
import { AlarmService } from "../../alarm.service";
import { ConfigService } from '../../../utils/config.service';
import { DisplayBehaviorService, E_DisplayBehavior } from '../../../utils/display-behavior.service';
import { C_DisplayData } from '../../../models/display/domain/display.model';
import { E_SocketOperation } from '../../../enums/socket/socket-operation.enum';
import { E_SocketMessageType } from '../../../enums/socket/socket-message-type.enum';
import { DisplaySocketProcessorService } from './display-socket-message.processor';
import { ObsUtilsService } from '../../../utils/obs-utils.service';
import { DisplaysService } from '../../displays.service';
import { E_WorkerType, WorkerPoolService } from '../../worker/worker-pool.service';
import { SmartRotativeLayoutsService } from '../../../../modules/main/pages/standard/pages/home/customization/smart-home/smart-rotative-layoutst.service';
import { BackendLocaleMap, E_SupportedLocales } from '../../../enums/locales.enum';
import { C_Token } from '../../../models/auth/token';


@Injectable(
  // {
  // providedIn: 'platform'
  // }
)
export class DisplayWsService {

  private utils = inject(UtilsService);
  private obsUtils = inject(ObsUtilsService);
  private auth = inject(AuthService);
  private alarmService = inject(AlarmService);
  private displayBehaviorService = inject(DisplayBehaviorService);
  private layoutsService = inject(SmartRotativeLayoutsService);
  private cfg = inject(ConfigService);
  private locale = inject(LOCALE_ID);
  private processor = inject(DisplaySocketProcessorService);
  private displayService = inject(DisplaysService);
  private ngZone = inject(NgZone);
  private workerPool = inject(WorkerPoolService);
  private destroy$ = new Subject<void>();

  // WS MANAGEMENT
  private isDisconnected = true;
  private isDisconnected$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  private connection?: HubConnection;
  private alphanumericCounter = new AlphanumericCounter();
  public connectionState$ = new BehaviorSubject<HubConnectionState>(HubConnectionState.Disconnected);
  private wsUrl: string = this.cfg.get("wsUrl") + '/subscribe';
  private reconnectTrigger$ = new BehaviorSubject<void>(undefined);
  // QUEUES
  private requestQueue$ = new Subject<I_WsRequest>();
  private messageStream$ = new Subject<{ type: E_SocketMessageType, message: any }>();
  private pendingRequestsSubject$: BehaviorSubject<I_WsRequest[]> = new BehaviorSubject<I_WsRequest[]>([]);
  private worker?: Worker;
  private taskIdWs = 'WS_PROCESSOR';

  // CURRENT DISPLAY
  private display: C_DisplayData = new C_DisplayData();
  private currentDisplayBehavior: E_DisplayBehavior = E_DisplayBehavior.Default;
  private currentSubscribedDisplayId: string | null = null;

  constructor() {
    this.initWorker();
    this.initMainWorkflow();
    this.initRequestQueue();
    this.initMessageStream();
    this.initNetworkStatusListeners();
  }

  /**
   * Método público para asegurar que la conexión se inicie al entrar en la app,
   * sin necesidad de estar suscrito a ningún display todavía.
   */
  public ensureConnection(): void {
    // Solo forzamos el trigger si actualmente estamos desconectados
    if (this.connectionState$.value === HubConnectionState.Disconnected) {
      this.reconnectTrigger$.next();
    }
  }

  /**
   * Function that returns the observable of the state for the disconnection with the websocket.
   */
  public hubStatus(): BehaviorSubject<HubConnectionState> {
    return this.connectionState$;
  }

  private initWorker() {
    this.worker = this.workerPool.getWorker({
      taskId: this.taskIdWs,
      type: E_WorkerType.SOCKET,
      weight: Infinity,
      isExclusive: true
    });

    this.worker.onmessage = ({ data }) => this.executeStateUpdate(data.type, data.payload);
  }

  executeStateUpdate(type: E_SocketMessageType, message: any) {
    if (!message) return;
    this.messageStream$.next({ type, message });
  }

  /**
   * FLUJO PRINCIPAL: Orquestación de Conexión + Lógica de Suscripción/Desuscripción
   */
  private initMainWorkflow() {
    // 1. FLUJO DE CONEXIÓN: Se asegura de conectar y mantener viva la conexión
    const connectionFlow$ = this.reconnectTrigger$.pipe(
      switchMap(() => this.connectToSignalR().pipe(
        catchError(err => {
          console.error("WS CONNECTION FAILURE. Retrying in 10s...", err);
          return timer(10000).pipe(
            tap(() => this.reconnectTrigger$.next()),
            switchMap(() => EMPTY)
          );
        })
      )),
      takeUntil(this.destroy$)
    );

    // 2. FLUJO DE NAVEGACIÓN: Reacciona a cambios de pantalla e interactúa con el Socket
    const subscriptionFlow$ = combineLatest([
      this.obsUtils.getCurrentDisplay().pipe(takeUntil(this.destroy$)),
      this.displayBehaviorService.getDisplayBehavior().pipe(takeUntil(this.destroy$))
    ]).pipe(
      throttleTime(25, asyncScheduler, { leading: false, trailing: true }),
      switchMap(([info, behavior]) => {
        // Esperamos a estar conectados para realizar operaciones de suscripción/desuscripción
        return this.connectionState$.pipe(
          filter(state => state === HubConnectionState.Connected),
          take(1),
          tap(() => this.handleDisplayChange(info, behavior)),
          switchMap(() => {
            // Heartbeat de modo Demo únicamente si hay pantalla activa
            if (behavior === E_DisplayBehavior.Demo && info && info.id) {
              return this.startDemoHeartbeat();
            }
            return NEVER;
          })
        );
      }),
      takeUntil(this.destroy$)
    );

    // Fusionamos de manera concurrente ambos flujos bajo el ciclo de vida del servicio
    merge(connectionFlow$, subscriptionFlow$)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  /**
     * Controla las transiciones de suscripciones al cambiar de pantalla o volver al main
     */
  private handleDisplayChange(info: C_DisplayData | null, behavior: E_DisplayBehavior) {
    const newDisplayId = info && info.id ? info.id : null;

    // Si no ha cambiado la pantalla actual, ignoramos
    if (this.currentSubscribedDisplayId === newDisplayId) {
      return;
    }

    // 1. Desuscripción de la pantalla anterior
    if (this.currentSubscribedDisplayId) {
      this.sendUnsubscribe(this.currentSubscribedDisplayId);
    }

    // 2. Suscripción a la nueva pantalla (o limpieza si volvemos al Main)
    if (newDisplayId && info) {
      this.display = info;
      this.currentDisplayBehavior = behavior;
      this.sendSubscribe(info, behavior);
      this.currentSubscribedDisplayId = newDisplayId;
    } else {
      // Regresamos al Main
      this.currentSubscribedDisplayId = null;
    }
  }

  // private reconnectWorkflow(info: C_DisplayData, behavior: E_DisplayBehavior): Observable<any> {
  //   return defer(() => {
  //     // Forzamos la limpieza del objeto antes de cada intento de creación
  //     if (this.connection) {
  //       this.connection.stop().catch(() => { });
  //       this.connection = undefined;
  //     }
  //     return this.connectToSignalR();
  //   }).pipe(
  //     tap(() => {
  //       this.layoutsService.setupHospitalFromDisplayData(info);
  //     }),
  //     switchMap(() => {
  //       return behavior === E_DisplayBehavior.Demo
  //         ? this.startDemoHeartbeat()
  //         : NEVER;
  //     }),
  //     retry({
  //       delay: (error) => {
  //         // Log crítico para depuración en producción
  //         console.error("WS CONNECTION FAILURE. Retrying in 10s...", error);
  //         return timer(10000).pipe(takeUntil(this.destroy$));
  //       }
  //     })
  //   );
  // }

  /**
   * COLA DE PETICIONES (Anti-Stuttering)
   */
  private initRequestQueue() {
    this.requestQueue$.pipe(
      concatMap(req => this.connectionState$.pipe(
        takeUntil(this.destroy$),
        filter(state => state === HubConnectionState.Connected),
        take(1),
        switchMap(() => from(this.sendSocketMessage(req))),
        delay(0)
      )),
      takeUntil(this.destroy$)
    ).subscribe();
  }


  /**
   * ENVÍO DE MENSAJES (Pure RxJS)
   */
  public sendSocketMessage(req: I_WsRequest): Observable<void> {
    const reqMessage: I_RequestMessage = {
      connectionId: this.connection?.connectionId,
      idMessage: this.alphanumericCounter.getNextId(),
      operation: req.operation,
      data: req.params,
    };

    return from(this.connection?.send(req.route || 'subscribe', JSON.stringify(reqMessage)) || EMPTY).pipe(
      takeUntil(this.destroy$),
      retry(2),
      tap({
        error: (err) => console.error('Error sending WS message', err)
      })
    );
  }

  /**
     * CONFIGURACIÓN DEL STREAM DE ENTRADA
     */
  private initMessageStream() {
    this.ngZone.runOutsideAngular(() => {
      this.messageStream$.pipe(
        takeUntil(this.destroy$)
      ).subscribe((item) => {
        this.processor.process(item.type, item.message, this.display);
        this.ngZone.run(() => {
          this.isDisconnected$.next(false);
        });
      });
    });
  }


  /**
   * GESTIÓN DE SIGNALR (Conexión persistente)
   */
  private connectToSignalR(): Observable<void> {
    // Si ya está conectado, resolvemos inmediatamente
    if (this.connection && this.connectionState$.value === HubConnectionState.Connected) {
      return of(undefined);
    }

    return from(this.auth.getToken()).pipe(
      takeUntil(this.destroy$),
      timeout(15000),
      switchMap((data: C_Token | null) => {
        // Detener conexión previa en caso de re-conexiones forzadas manuales
        const stop$ = this.connection
          ? from(this.connection.stop()).pipe(catchError(() => of(null)))
          : of(null);
        return stop$.pipe(map(() => data));
      }),
      switchMap((data: C_Token | null) => {
        if (!data?.accessToken) {
          return throwError(() => new Error("No hay token de acceso"));
        }

        const url = `${this.wsUrl}`;
        this.connection = new HubConnectionBuilder()
          .withUrl(url, {
            transport: HttpTransportType.WebSockets,
            accessTokenFactory: () => data.accessToken!
          })
          .withAutomaticReconnect({
            nextRetryDelayInMilliseconds: () => 5000
          })
          .build();

        this.connection.onreconnecting((error) => {
          this.ngZone.run(() => {
            console.warn("SignalR: Reconectando...", error);
            this.connectionState$.next(HubConnectionState.Reconnecting);
          });
        });

        this.connection.onreconnected((connectionId) => {
          this.ngZone.run(() => {
            console.log("SignalR: Reconectado", connectionId);
            this.connectionState$.next(HubConnectionState.Connected);
            this.sendInitialSubscribe(); // Recuperamos la suscripción activa tras reconectarse
          });
        });

        this.connection.onclose(() => {
          this.ngZone.run(() => {
            this.connectionState$.next(HubConnectionState.Disconnected);
          });
        });

        this.connection.on('ReceiveMessage', (type, data) => this.handleMessage(type, data));

        return from(this.connection.start()).pipe(
          timeout(20000),
          tap(() => {
            this.connectionState$.next(HubConnectionState.Connected);
            this.sendInitialSubscribe();
          }),
          catchError(err => {
            this.connectionState$.next(HubConnectionState.Disconnected);
            return throwError(() => err);
          })
        );
      })
    );
  }

  /**
     * Recupera de manera automática la suscripción activa tras caídas de conexión
     */
  private sendInitialSubscribe() {
    if (this.currentSubscribedDisplayId && this.display) {
      this.sendSubscribe(this.display, this.currentDisplayBehavior);
    }
  }

  /**
     * Helpers para construir los payloads de suscripción y desuscripción
     */
  private sendSubscribe(info: C_DisplayData, behavior: E_DisplayBehavior) {
    const params: I_WebsocketParams = {
      displayId: info.id,
      sectionInfo: true,
      recordingAlerts: false,
      treatments: true,
      appointments: false,
      diagnosis: false,
      userName: this.auth.getUser()?.userName ?? '',
      relay: false,
      locale: BackendLocaleMap[this.locale as E_SupportedLocales],
      isDemo: behavior === E_DisplayBehavior.Demo,
    };
    this.requestData(E_SocketOperation.DISPLAY_SUBSCRIBE, params);
    this.layoutsService.setupHospitalFromDisplayData(info);
  }

  private sendUnsubscribe(displayId: string) {
    const params: any = null;
    // const params: I_WebsocketParams = {
    //   displayId: null,
    //   sectionInfo: false,
    //   recordingAlerts: false,
    //   treatments: false,
    //   appointments: false,
    //   diagnosis: false,
    //   userName: this.auth.getUser()?.userName ?? '',
    //   relay: false,
    //   locale: BackendLocaleMap[this.locale as E_SupportedLocales],
    //   isDemo: false,
    // };

    const unsubscribeOp = E_SocketOperation.DISPLAY_SUBSCRIBE;
    this.requestData(unsubscribeOp, params);
  }

  public requestData(operation: string, params: I_WebsocketParams, route: string = 'subscribe'): void {
    this.requestQueue$.next({ operation, params, route });
  }

  /**
   * MODO DEMO (Heartbeat reactivo)
   */
  private startDemoHeartbeat(): Observable<never> {
    return timer(2000, 50000).pipe(
      tap(() => this.sendDemoHeartbeatMessage(true)),
      finalize(() => {
        // Se ejecuta automáticamente cuando el switchMap cancela este stream (salida de modo Demo)
        // Validamos estar conectados y tener pantalla activa para evitar envíos residuales en Logout/Destroy
        if (this.connectionState$.value === HubConnectionState.Connected && this.currentSubscribedDisplayId) {
          this.sendDemoHeartbeatMessage(false);
        }
      }),
      takeUntil(this.destroy$),
      switchMap(() => EMPTY) // Mantiene el stream vivo sin emitir basura
    );
  }

  /**
   * Helper unificado para el envío del estado de Demo
   */
  private sendDemoHeartbeatMessage(isDemo: boolean): void {
    this.requestData(E_SocketOperation.DEMO, {
      isDemo,
      displayId: this.display.id,
      userName: this.auth.getUser()?.userName ?? '',
    });
  }

  private handleMessage(type: E_SocketMessageType, data: any) {
    // FORZAR ESTADO: Si recibimos algo, estamos conectados.
    if (this.connectionState$.value !== HubConnectionState.Connected) {
      this.connectionState$.next(HubConnectionState.Connected);
    }
    // Si el worker está activo, delegamos el parseo y resolveReferences
    if (this.worker) {
      this.worker.postMessage({ type, rawData: data });
    } else {
      // Fallback si no hay worker (parseo básico)
      this.executeStateUpdate(type, this.utils.jsonParse(data));
    }
  }

  /**
   * Function that returns the observable of the state for the disconnection with the websocket.
   */
  public getIsDisconnected(): Observable<boolean> {
    return this.connectionState$.pipe(
      takeUntil(this.destroy$),
      // Mapeamos: solo estamos "conectados" si el estado es explícitamente Connected
      map(state => state !== HubConnectionState.Connected),
      distinctUntilChanged(),
      switchMap(isDisconnected => {
        if (isDisconnected) {
          return timer(1000).pipe(map(() => true), takeUntil(this.destroy$));
        }
        return of(false);
      }),
      distinctUntilChanged(),
      // Forzamos que la emisión entre en la zona de Angular para que OnPush se entere
      o => new Observable<boolean>(subscriber => {
        const sub = o.subscribe({
          next: val => this.ngZone.run(() => subscriber.next(val)),
          error: err => this.ngZone.run(() => subscriber.error(err)),
          complete: () => this.ngZone.run(() => subscriber.complete())
        });
        return () => sub.unsubscribe();
      })
    );
  }

  private initNetworkStatusListeners() {
    merge(
      fromEvent(window, 'online').pipe(map(() => true)).pipe(takeUntil(this.destroy$)),
      fromEvent(window, 'offline').pipe(map(() => false)).pipe(takeUntil(this.destroy$)),
    ).pipe(
      takeUntil(this.destroy$)
    ).subscribe(isOnline => {
      if (isOnline) {
        this.reconnectTrigger$.next();
      } else {
        if (this.connection) {
          this.connection.stop().catch(() => { });
        }
        this.connectionState$.next(HubConnectionState.Disconnected);
      }
    });
  }
  // Method to cancel all pending requests
  clearPendingRequests(): void {
    this.pendingRequestsSubject$.next([]);
  }

  /**
   * Flujo de salida: Cierra físicamente la conexión y limpia todo el estado.
   * Llama a este método al hacer Logout.
   */
  public logout() {
    this.reset();
  }

  /**
   * Limpieza profunda del servicio
   */
  reset() {
    this.displayService.removeDisplay();

    if (this.connection) {
      this.connection.stop().catch(() => { });
      this.connection = undefined;
    }
    this.connectionState$.next(HubConnectionState.Disconnected);
    this.pendingRequestsSubject$.next([]);
    this.isDisconnected = true;
    this.isDisconnected$.next(this.isDisconnected);

    // Reseteamos estados locales de suscripciones
    this.currentSubscribedDisplayId = null;
    this.display = new C_DisplayData();

    this.display.resetSubs();
    this.alarmService.resetPlayBackInterval();
  }

  ngOnDestroy() {
    this.displayService.removeDisplay();
    this.destroy$.next();
    this.destroy$.complete();
    this.reset();
    this.connectionState$.complete();
    this.requestQueue$.complete();
    this.messageStream$.complete();
    this.pendingRequestsSubject$.complete();
    this.isDisconnected$.complete();
    this.workerPool.releaseWorker(this.taskIdWs);
  }



}

export interface I_WsRequest {
  operation: string;
  params: I_WebsocketParams;
  route?: string;
}
